/**
 * The covenant tests, this period against the last one.
 *
 * Headroom is the number the credit committee actually asks for, so it gets
 * its own columns and its own colour: amber when the test is near its
 * threshold, red when it is broken. The Change column says which way the
 * headroom moved in words ("tightened 0.39x") because a signed delta on a test
 * that can be `<=` or `>=` reads backwards half the time.
 *
 * Without a locked prior period the compare endpoint 404s; the table then
 * renders the current columns only rather than a row of em dashes pretending
 * there was something to compare against.
 */

import { TrendingDown, TrendingUp } from "lucide-react";
import React from "react";

import StatusPill from "../StatusPill";
import type {
	MonitoringCompare,
	MonitoringCovenantOutcome,
	MonitoringCovenantResult,
	MonitoringRun,
} from "../../types/monitoring";
import { formatFigure as formatCovenantFigure } from "./monitoringFormat";
import { formatFigure, formatMagnitude } from "./runFormat";

/** "0.37x over the limit" / "0.31x of headroom" / "waits on a figure". */
export function headroomWords(result: MonitoringCovenantResult): string {
	if (result.headroom === null) return "waits on a figure that isn't in yet";
	const amount = formatCovenantFigure(Math.abs(result.headroom), result.unit);
	return result.headroom < 0 ? `${amount} over the limit` : `${amount} of headroom`;
}

export interface CovenantsCompareProps {
	run: MonitoringRun;
	/** Null when there is no locked prior period to compare against. */
	compare: MonitoringCompare | null;
}

const OUTCOME_PILL: Record<
	MonitoringCovenantOutcome,
	{ tone: "green" | "amber" | "red" | "grey"; label: string }
> = {
	pass: { tone: "green", label: "Pass" },
	near: { tone: "amber", label: "Near" },
	breach: { tone: "red", label: "Breach" },
	unknown: { tone: "grey", label: "Unknown" },
};

const HEADROOM_CLASS: Record<MonitoringCovenantOutcome, string> = {
	pass: "text-[#111111]",
	near: "text-[#111111]",
	breach: "text-[#b42318]",
	unknown: "text-[#9a9ea3]",
};

const DOT: Record<MonitoringCovenantOutcome, string> = {
	pass: "bg-[#067647]",
	near: "bg-[#b54708]",
	breach: "bg-[#b42318]",
	unknown: "bg-[#9a9ea3]",
};

/** "≤ 5.25x · from credit agreement" — the test as the agreement states it. */
function testLine(
	operator: "<=" | ">=",
	required: number | null,
	unit: MonitoringCovenantResult["unit"],
): string {
	const symbol = operator === "<=" ? "≤" : "≥";
	return `${symbol} ${formatFigure(required, unit)} · credit agreement`;
}

interface Row {
	test_key: string;
	label: string;
	operator: "<=" | ">=";
	unit: MonitoringCovenantResult["unit"];
	current: MonitoringCovenantResult;
	prior: MonitoringCovenantResult | null;
	headroom_delta: number | null;
}

const CovenantsCompare: React.FC<CovenantsCompareProps> = ({ run, compare }) => {
	const rows: Row[] = compare
		? compare.covenants.map((covenant) => ({
				test_key: covenant.test_key,
				label: covenant.label,
				operator: covenant.operator,
				unit: covenant.unit,
				current: covenant.current,
				prior: covenant.prior,
				headroom_delta: covenant.headroom_delta,
			}))
		: run.covenants.map((covenant) => ({
				test_key: covenant.test_key,
				label: covenant.label,
				operator: covenant.operator,
				unit: covenant.unit,
				current: covenant,
				prior: null,
				headroom_delta: null,
			}));

	const hasPrior = compare !== null;
	const priorLabel = compare?.prior.period.label ?? "Prior";
	const moved = (compare?.figures ?? []).filter(
		(figure) => figure.delta !== null && figure.delta !== 0,
	);

	if (rows.length === 0) {
		return (
			<p className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-text-muted">
				This template has no covenant tests.
			</p>
		);
	}

	// Largest relative move first: the figures most likely behind a change in
	// the tests. A move with no percentage (from zero) goes last.
	const movedByEffect = [...moved].sort(
		(a, b) => Math.abs(b.delta_pct ?? 0) - Math.abs(a.delta_pct ?? 0),
	);

	return (
		<div className="flex flex-col gap-5 lg:flex-row lg:items-start">
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<p className="text-sm text-[#5B6472]">
					{hasPrior
						? `Against the credit agreement's tests, compared with ${priorLabel}.`
						: "Against the credit agreement's tests. The comparison appears once an earlier period is locked."}
				</p>
				{rows.map((row) => {
					const outcome = OUTCOME_PILL[row.current.result];
					const actual = row.current.actual;
					const required = row.current.required;
					const priorActual = row.prior?.actual ?? null;
					const delta = row.headroom_delta;
					const points = [actual, required, priorActual].filter(
						(value): value is number => value !== null,
					);
					const low = Math.min(...points);
					const high = Math.max(...points);
					const pad = (high - low || Math.abs(high) || 1) * 0.25;
					const at = (value: number) =>
						`${Math.min(96, Math.max(4, ((value - (low - pad)) / (high - low + 2 * pad)) * 100))}%`;
					return (
						<section
							key={row.test_key}
							aria-label={row.label}
							data-testid={`covenant-${row.test_key}`}
							className={`grid items-center gap-6 rounded-xl border bg-white px-[18px] py-4 md:grid-cols-[minmax(0,1.3fr)_minmax(0,2fr)_minmax(0,1fr)] ${
								row.current.result === "breach" ? "border-[#fecdca]" : "border-gray-200"
							}`}
						>
							<div>
								<p className="text-[15px] font-semibold text-[#111111]">{row.label}</p>
								<p className="mt-0.5 text-[12.5px] text-[#6B7280]">
									Must stay {testLine(row.operator, required, row.unit)}
								</p>
								<div className="mt-2.5">
									<StatusPill tone={outcome.tone} label={`${outcome.label} · ${headroomWords(row.current)}`} />
								</div>
							</div>
							<div aria-hidden>
								{actual !== null && required !== null ? (
									<>
										<div className="relative h-2.5 rounded-full bg-[#f1f2f4]">
											<span className="absolute -top-1.5 h-[22px] w-0.5 bg-[#111111]" style={{ left: at(required) }} />
											{priorActual !== null && (
												<span
													className="absolute -top-[3px] h-4 w-4 -translate-x-1/2 rounded-full border-[3px] border-white bg-[#9a9ea3]"
													style={{ left: at(priorActual) }}
												/>
											)}
											<span
												className={`absolute -top-[3px] h-4 w-4 -translate-x-1/2 rounded-full border-[3px] border-white ${DOT[row.current.result]}`}
												style={{ left: at(actual) }}
											/>
										</div>
										<p className="mt-2.5 flex justify-between text-[11.5px] text-[#6B7280]">
											<span>limit {formatFigure(required, row.unit)}</span>
											{priorActual !== null && (
												<span>
													{priorLabel} {formatFigure(priorActual, row.unit)} (grey)
												</span>
											)}
										</p>
									</>
								) : (
									<p className="text-[12.5px] text-[#6B7280]">Waiting on a figure before it can be tested.</p>
								)}
							</div>
							<div className="text-right">
								<p className={`text-2xl font-bold tabular-nums ${HEADROOM_CLASS[row.current.result]}`}>
									{formatFigure(actual, row.unit)}
								</p>
								{delta !== null && delta !== 0 && (
									<p
										className={`mt-0.5 inline-flex items-center gap-1 text-[12.5px] ${
											delta < 0 ? "text-[#b42318]" : "text-[#067647]"
										}`}
									>
										{delta < 0 ? (
											<TrendingDown className="h-3.5 w-3.5" aria-hidden="true" />
										) : (
											<TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
										)}
										{delta < 0 ? "tightened" : "widened"} {formatMagnitude(delta, row.unit)}
									</p>
								)}
								{hasPrior && delta === 0 && <p className="mt-0.5 text-[12.5px] text-[#5B6472]">Same as {priorLabel}</p>}
							</div>
						</section>
					);
				})}
			</div>

			{compare && (
				<aside
					aria-label="What moved"
					className="w-full self-start rounded-xl border border-gray-200 bg-white px-[18px] py-4 lg:w-[300px] lg:shrink-0"
				>
					<h2 className="text-sm font-semibold text-[#111111]">What moved since {priorLabel}</h2>
					<p className="mt-0.5 text-[12.5px] text-[#6B7280]">The figures behind the tests, largest move first</p>
					{movedByEffect.length === 0 ? (
						<p className="mt-2 text-sm text-[#5B6472]">No figure changed against {priorLabel}.</p>
					) : (
						<ul className="mt-2">
							{movedByEffect.map((figure) => (
								<li
									key={figure.key}
									className="flex justify-between gap-3 border-t border-[#f1f2f4] py-2 text-[13px]"
									data-testid={`moved-${figure.key}`}
								>
									<span className="text-[#3f4247]">{figure.label}</span>
									<span className="tabular-nums">
										<span className="text-[#6B7280]">{formatFigure(figure.prior, figure.unit)} →</span>{" "}
										<b className="text-[#111111]">{formatFigure(figure.current, figure.unit)}</b>
									</span>
								</li>
							))}
						</ul>
					)}
					<p className="mt-2.5 rounded-lg bg-[#f7f7f6] px-3 py-2.5 text-[12.5px] leading-normal text-[#3f4247]">
						A breach does not stop you locking the period. The lock records it with the figures behind it.
					</p>
				</aside>
			)}
		</div>
	);
};

export default CovenantsCompare;
